import isObject from 'lodash/isObject';
import OwnerService from 'aurora-composition/services/ownerService';
import WorkflowService from 'aurora-composition/services/workflowService';
import MortgageService from 'aurora-composition/services/mortgageService';
import ContractService from 'aurora-composition/services/contractsService';
import AuthService from 'aurora-composition/services/authService';
import BuyersListService from 'aurora-composition/services/buyersListService';
import FILTERS_NAMES from 'aurora-utils/filtersNames';
import { transformDropDownItems } from 'aurora-utils/utils';
import isArray from 'lodash/isArray';
import * as types from './constants';

const getFilterData = filterName => {
  switch (filterName) {
    case FILTERS_NAMES.POA_OWNERS:
      return {
        responseDataKey: 'result',
        apiCallHandler: OwnerService.fetchPoaCustomers,
        valueKey: 'userId',
      };
    case FILTERS_NAMES.PROJECT_NAME:
      return {
        responseDataKey: 'projects',
        apiCallHandler: AuthService.fetchPropertyProjects,
        valueKey: 'projectId',
      };
    case FILTERS_NAMES.PROJECT_NAME_MORTGAGE:
      return {
        responseDataKey: 'projects',
        apiCallHandler: AuthService.fetchPropertyProjects,
        valueKey: 'nameEn',
      };
    case FILTERS_NAMES.UNIT_USE:
      return {
        responseDataKey: 'unitUses',
        apiCallHandler: AuthService.fetchOffPlanUnitUsage,
        valueKey: 'unitUseId',
      };
    case FILTERS_NAMES.LAND_USE:
      return {
        responseDataKey: 'landUses',
        apiCallHandler: AuthService.fetchOffPlanLandUsage,
        valueKey: 'landUseID',
        labelEn: 'landUseNameEn',
        labelAr: 'landUseNameAr',
      };
    case FILTERS_NAMES.CERTIFICATE_TYPE_V2:
      return {
        responseDataKey: 'result',
        apiCallHandler: AuthService.fetchCertificateType,
        valueKey: 'applicationType',
        labelEn: 'serviceNameEn',
        labelAr: 'serviceNameAr',
      };
    case FILTERS_NAMES.CERTIFICATE_TYPE_LAND:
      return {
        responseDataKey: 'result',
        apiCallHandler: AuthService.fetchCertificateType,
        valueKey: 'applicationType',
        labelEn: 'serviceNameEn',
        labelAr: 'serviceNameAr',
      };
    case FILTERS_NAMES.CERTIFICATE_TYPE_UNIT:
      return {
        responseDataKey: 'result',
        apiCallHandler: AuthService.fetchCertificateType,
        valueKey: 'applicationType',
        labelEn: 'serviceNameEn',
        labelAr: 'serviceNameAr',
      };
    case FILTERS_NAMES.CERTIFICATE_TYPE_LAND_BROKER:
      return {
        responseDataKey: 'result',
        apiCallHandler: AuthService.fetchCertificateType,
        valueKey: 'applicationType',
        labelEn: 'serviceNameEn',
        labelAr: 'serviceNameAr',
      };
    case FILTERS_NAMES.CERTIFICATE_TYPE_UNIT_BROKER:
      return {
        responseDataKey: 'result',
        apiCallHandler: AuthService.fetchCertificateType,
        valueKey: 'applicationType',
        labelEn: 'serviceNameEn',
        labelAr: 'serviceNameAr',
      };
    case FILTERS_NAMES.UNIT_TYPE:
      return {
        responseDataKey: 'unitTypes',
        apiCallHandler: AuthService.fetchOffPlanUnitTypes,
        valueKey: 'unitTypeId',
      };
    case FILTERS_NAMES.MORTGAGE_TYPE:
      return {
        responseDataKey: 'items',
        apiCallHandler: MortgageService.fetchMortgageTypes,
        valueKey: 'id',
      };
    case FILTERS_NAMES.CONTRACT_CLASSIFICATION_ID:
      return {
        responseDataKey: 'result',
        apiCallHandler: ContractService.fetchContractTypes,
        valueKey: 'id',
      };
    case FILTERS_NAMES.APPLICATION_TYPE:
      return {
        responseDataKey: 'applicationTypes',
        apiCallHandler: WorkflowService.fetchApplicationTypes,
        valueKey: 'applicationType',
        labelEn: 'applicationTypeNameEn',
        labelAr: 'applicationTypeNameAr',
      };
    case FILTERS_NAMES.NATIONALITY_ID:
      return {
        responseDataKey: 'nationalities',
        apiCallHandler: BuyersListService.getNationalities,
        valueKey: 'nationalityid',
        labelEn: 'nationalitynameEn',
        labelAr: 'nationalitynameAr',
      };
    default:
      return null;
  }
};

export const fetchFilterData = (filterName, params) => async (dispatch, getState) => {
  try {
    const { locale } = getState().language;
    dispatch({
      type: types.FETCH_FILTER_DATA,
    });

    const filterData = getFilterData(filterName);
    const response = await filterData?.apiCallHandler(params);
    const { responseDataKey, valueKey, labelEn, labelAr } = filterData;
    const data = transformDropDownItems(
      response[responseDataKey],
      locale === 'en' ? labelEn || 'nameEn' : labelAr || 'nameAr',
      valueKey,
    );

    dispatch({
      type: types.FETCH_FILTER_DATA_SUCCESS,
      payload: {
        name: filterName,
        data,
      },
    });
  } catch (error) {
    dispatch({
      type: types.FETCH_FILTER_DATA_FAILURE,
      payload: {
        error,
      },
    });
  }
};
export const clearFilterData = filterName => async dispatch => {
  dispatch({
    type: types.CLEAR_FILTER_DATA,
    payload: {
      name: filterName,
    },
  });
};

export const fetchFiltersDropDowns = (filtersNames, filtersParams = {}) => async (dispatch, getState) => {
  dispatch({
    type: types.FETCH_FILTERS_DROPDOWNS,
  });
  const { locale } = getState().language;
  const filtersData = filtersNames?.reduce((result, filterName) => {
    const updatedResult = [...result];
    const filterData = getFilterData(filterName);

    if (filterData !== null) {
      updatedResult?.push({ name: filterName, ...filterData });
    }
    return updatedResult;
  }, []);

  const filtersPromises = filtersData?.map(filterData => {
    const filterParams = isObject(filtersParams) ? filtersParams[filterData?.name] : undefined;
    return filterData?.apiCallHandler(filterParams);
  });

  const responseArray = await Promise.allSettled(filtersPromises);
  const dropDowns = {};
  responseArray?.forEach((resData, index) => {
    if (resData?.status === 'fulfilled') {
      const name = filtersData[index]?.name;
      const { responseDataKey, valueKey, labelEn, labelAr } = filtersData[index];
      const dataList = isArray(resData?.value[responseDataKey])
        ? resData?.value[responseDataKey]
        : resData?.value?.result[responseDataKey];
      if (isArray(dataList) && dataList?.length > 0) {
        let filterDataList = dataList;
        if (name === 'certificateTypePermitsLand' || name === 'certificateTypePermitsLandBroker') {
          filterDataList = dataList?.filter(
            item =>
              item?.applicationType !== 'CERT_PROP_OWNERSHIP' &&
              !['CERT_VERIFICATION_UNIT', 'CERT_TITLE_DEED_UNIT', 'REQUEST_VALUATION_CERTIFICATE_UNIT'].includes(
                item?.applicationType,
              ),
          );
          if (name === 'certificateTypePermitsLandBroker') {
            filterDataList.push({
              serviceId: 10171,
              applicationType: 'OFF_PLAN_FIRST_SELL_LAND',
              serviceNameEn: 'Off-Plan Agreement Land',
              serviceNameAr: 'اتفاقية أرض على الخارطة',
            });
          }
        }

        if (name === 'certificateTypePermitsUnit' || name === 'certificateTypePermitsUnitBroker') {
          filterDataList = dataList?.filter(
            item =>
              item?.applicationType !== 'CERT_PROP_OWNERSHIP' &&
              ![
                'CERT_SITE_PLAN_PLOT',
                'CERT_TITLE_DEED_PLOT',
                'CERT_VERIFICATION_PLOT',
                'REQUEST_VALUATION_CERTIFICATE_LAND',
              ].includes(item?.applicationType),
          );
          if (name === 'certificateTypePermitsUnitBroker') {
            filterDataList.push({
              serviceId: 10171,
              applicationType: 'OFF_PLAN_FIRST_SELL_UNIT',
              serviceNameEn: 'Off-Plan Agreement Unit',
              serviceNameAr: 'اتفاقية وحدة على الخارطة',
            });
          }
        }

        dropDowns[name] = transformDropDownItems(
          filterDataList,
          locale === 'en' ? labelEn || 'nameEn' : labelAr || 'nameAr',
          valueKey,
        );
      } else {
        dropDowns[name] = [];
      }
    }
  });

  const error = Object.keys(dropDowns)?.length !== filtersData?.length;

  dispatch({
    type: types.FETCH_FILTERS_DROPDOWNS_SUCCESS,
    payload: {
      dropDowns,
      error,
    },
  });
};
